import { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FaDownload } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  bulkUploadGlobalBlockContact,
  resetBulkUploadResult,
} from '../../store/slices/blockContacts/create';

const SAMPLE_CSV_CONTENT = {
  Mobile: 'Mobile Number\n9812345671\n9876543210\n9123456780',
  Email: 'Email\nexample1@test.com\nexample2@test.com\nexample3@test.com',
};

// Bulk CSV import of Mobile/Email numbers into the Global Block list.
// Only meaningful for scopeType='Global' - Campaign/Tags scoped blocks are
// tied to a single campaign/tag and are out of scope for this uploader.
const BlockContactBulkUploadGlobalBlock = (props) => {
  const { blockType } = props;
  const [file, setFile] = useState(null);
  const dispatch = useDispatch();

  const { bulkUploading, bulkUploadResult } = useSelector(
    (state) => state.blockContactCreatetSlice
  );

  useEffect(() => {
    dispatch(resetBulkUploadResult());
  }, [dispatch]);

  const fileInputId = `blockContactBulkUploadFile-${blockType}`;

  const fileChangeHandler = (event) => {
    setFile(event.target.files[0] || null);
  };

  const downloadSampleHandler = () => {
    const csvContent = SAMPLE_CSV_CONTENT[blockType] || SAMPLE_CSV_CONTENT.Mobile;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `sample_global_block_${blockType.toLowerCase()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const uploadHandler = async () => {
    if (!file) {
      toast.error('Please choose a CSV file to upload.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('blockType', blockType);

    const result = await dispatch(bulkUploadGlobalBlockContact(formData));
    if (bulkUploadGlobalBlockContact.fulfilled.match(result)) {
      setFile(null);
      const fileInput = document.getElementById(fileInputId);
      if (fileInput) fileInput.value = '';
    }
  };

  return (
    <div className="border rounded p-3 mb-3 bg-light">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label htmlFor={fileInputId} className="fw-bold mb-0">
          Or bulk upload via CSV
        </label>
        <Button variant="link" size="sm" type="button" className="p-0" onClick={downloadSampleHandler}>
          <FaDownload className="me-1" />
          Download Sample CSV
        </Button>
      </div>
      <div className="d-flex align-items-center gap-2">
        <input
          type="file"
          accept=".csv"
          id={fileInputId}
          className="form-control"
          onChange={fileChangeHandler}
        />
        <Button
          variant="outline-primary"
          type="button"
          onClick={uploadHandler}
          disabled={bulkUploading}
        >
          {bulkUploading ? <Spinner size="sm" animation="border" /> : 'Upload CSV'}
        </Button>
      </div>
      <small className="text-muted d-block mt-1">
        CSV should have one {blockType === 'Email' ? 'email address' : 'mobile number'} per row
        (header row optional). Numbers already blocked are skipped automatically.
      </small>

      {bulkUploadResult?.data && (
        <div className="mt-2 small">
          <span className="text-success me-3">
            Added: {bulkUploadResult.data.insertedCount}
          </span>
          <span className="text-warning me-3">
            Already blocked: {bulkUploadResult.data.skippedExisting?.length || 0}
          </span>
          <span className="text-danger">
            Invalid rows: {bulkUploadResult.data.invalidRows?.length || 0}
          </span>
        </div>
      )}
    </div>
  );
};

export default BlockContactBulkUploadGlobalBlock;
